import { useState, useEffect, useRef } from 'react'
import { Camera, Loader2, Trash2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import { UserProfileApi, UserProfile } from '@/shared/api/users'
import { getUserBannerUrl } from '@/shared/utils/userImages'
import { useAuth } from '@/modules/auth'
import { BannerCropModal } from '@/modules/users/components/BannerCropModal'

interface DashboardCoverProps {
  userName: string
}

export function DashboardCover({ userName }: DashboardCoverProps) {
  const { user, getAccessToken } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showCropModal, setShowCropModal] = useState(false)
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!user?.id) return

    const loadProfile = async () => {
      setIsLoading(true)
      try {
        const userProfileApi = new UserProfileApi(getAccessToken)
        const userProfile = await userProfileApi.getUserProfile(user.id)
        setProfile(userProfile)
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    }

    loadProfile()
  }, [user?.id, getAccessToken])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB')
      return
    }

    // Create preview for crop modal
    const reader = new FileReader()
    reader.onloadend = () => {
      const imageSrc = reader.result as string
      setSelectedImageSrc(imageSrc)
      setShowCropModal(true)
    }
    reader.readAsDataURL(file)
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropComplete = async (croppedFile: File) => {
    if (!user?.id) return

    setIsUploading(true)
    try {
      const userProfileApi = new UserProfileApi(getAccessToken)
      const updated = await userProfileApi.uploadBannerImage(user.id, croppedFile)
      setProfile(updated)
      setShowCropModal(false)
      setSelectedImageSrc(null)
    } catch (error) {
      alert('Failed to upload cover photo. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleBannerDelete = async () => {
    if (!user?.id || !profile) return
    if (!confirm('Are you sure you want to delete your cover photo?')) return

    setIsDeleting(true)
    try {
      const userProfileApi = new UserProfileApi(getAccessToken)
      await userProfileApi.deleteBannerImage(user.id)
      const updated = await userProfileApi.getUserProfile(user.id)
      setProfile(updated)
    } catch (error) {
      alert('Failed to delete cover photo. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const bannerUrl = profile ? getUserBannerUrl(profile.banner_image_path) : ''

  if (isLoading) {
    return (
      <div className="relative w-full h-40 md:h-48 bg-adventure-200 overflow-hidden">
        <div className="w-full h-full bg-gradient-to-br from-adventure-100 to-adventure-300 animate-pulse" />
      </div>
    )
  }

  return (
    <>
      <div className="relative w-full h-40 md:h-48 overflow-hidden bg-adventure-200 group">
        {/* Background image */}
        {bannerUrl ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bannerUrl})` }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-adventure-100 to-adventure-300" />
        )}

        {/* Bottom scrim for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

        {/* Content overlay - bottom-left anchored */}
        <div className="absolute inset-0 flex flex-col justify-end items-start p-4 md:p-6 z-10">
          <h1 className="text-xl md:text-2xl font-semibold text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">
            Welcome back, {userName}
          </h1>
          <p className="text-xs md:text-sm text-white/85 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
            Ready for your next adventure?
          </p>
        </div>

        {/* Upload/Edit button - appears on hover */}
        <div className="absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isDeleting}
              variant="secondary"
              size="sm"
              className="bg-white/90 dark:bg-card/90 backdrop-blur-sm hover:bg-white dark:hover:bg-card shadow-lg text-earth-900 dark:text-foreground"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 mr-2" />
                  {bannerUrl ? 'Change' : 'Upload Cover'}
                </>
              )}
            </Button>
            {bannerUrl && (
              <Button
                onClick={handleBannerDelete}
                disabled={isUploading || isDeleting}
                variant="destructive"
                size="sm"
                className="bg-red-500/90 backdrop-blur-sm hover:bg-red-600 shadow-lg"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {showCropModal && selectedImageSrc && (
        <BannerCropModal
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false)
            setSelectedImageSrc(null)
          }}
          imageSrc={selectedImageSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={16 / 9} // Desktop background aspect ratio (wider than banner)
        />
      )}
    </>
  )
}

